import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ReturnFeesAllowancesComponent } from '../components/return-fees-allowances/return-fees-allowances.component';
import { ReturnNotesComponent } from '../components/return-notes/return-notes.component';
import { ReturnAttachmentsComponent } from '../components/return-attachments/return-attachments.component';
import { ReturnHistoryComponent } from '../components/return-history/return-history.component';
import { ReturnModel } from '../../../domain/models/return.model';

export type ReturnSettingsType = 'fees' | 'notes' | 'attachments' | 'history';

export interface ReturnSettingsDialogData {
  type: ReturnSettingsType;
  returnData: ReturnModel;
  returnId: number;
}

/**
 * Modal host for the return sub-sections (Fee/Allowances, Notes, Attachments, History),
 * reusing the existing standalone components. Returns `true` on close when the data was
 * modified so the caller can refresh totals/counts.
 */
@Component({
  selector: 'app-return-settings-dialog',
  standalone: true,
  imports: [
    CommonModule, MatDialogModule, MatButtonModule, MatIconModule,
    ReturnFeesAllowancesComponent, ReturnNotesComponent,
    ReturnAttachmentsComponent, ReturnHistoryComponent,
  ],
  template: `
    <div class="dialog-head">
      <span class="dialog-title"><mat-icon>{{ icon }}</mat-icon> {{ title }}</span>
      <button mat-icon-button (click)="close()" aria-label="Close"><mat-icon>close</mat-icon></button>
    </div>
    <div mat-dialog-content class="dialog-body">
      <app-return-fees-allowances *ngIf="data.type === 'fees'"
        [returnData]="data.returnData" (feesUpdated)="onFeesUpdated($event)"></app-return-fees-allowances>
      <app-return-notes *ngIf="data.type === 'notes'"
        [returnId]="data.returnId" [notes]="data.returnData.notes || []"></app-return-notes>
      <app-return-attachments *ngIf="data.type === 'attachments'"
        [returnId]="data.returnId" [attachments]="data.returnData.attachments || []"></app-return-attachments>
      <app-return-history *ngIf="data.type === 'history'"
        [returnId]="data.returnId" [history]="data.returnData.history || []"></app-return-history>
    </div>
    <div mat-dialog-actions align="end">
      <button mat-stroked-button (click)="close()">CLOSE</button>
    </div>
  `,
  styles: [`
    .dialog-head { display: flex; justify-content: space-between; align-items: center; padding: 8px 8px 0 16px; }
    .dialog-title { display: flex; align-items: center; gap: 8px; font-weight: 600; color: #334155; }
    .dialog-body { min-width: 520px; max-width: 760px; }
    @media (max-width: 600px) { .dialog-body { min-width: auto; } }
  `],
})
export class ReturnSettingsDialogComponent {
  dirty = false;

  private readonly titles: Record<ReturnSettingsType, string> = {
    fees: 'Fee & Allowances', notes: 'Notes', attachments: 'Attachments', history: 'History',
  };
  private readonly icons: Record<ReturnSettingsType, string> = {
    fees: 'attach_money', notes: 'note', attachments: 'attach_file', history: 'history',
  };

  constructor(
    public dialogRef: MatDialogRef<ReturnSettingsDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: ReturnSettingsDialogData
  ) {}

  get title(): string { return this.titles[this.data.type]; }
  get icon(): string { return this.icons[this.data.type]; }

  close(): void { this.dialogRef.close(this.dirty); }

  /** An added charge is not in this dialog's copy of the return: close so the page reloads and shows it. */
  onFeesUpdated(added: boolean): void {
    this.dirty = true;
    if (added) { this.close(); }
  }
}
